import { Fragment } from "react"
import type { FC, ReactElement } from "react"

import { AnimatePresence, LazyMotion, domAnimation } from "framer-motion"
import eq from "lodash-es/eq"
import map from "lodash-es/map"
import { useShallowCompareEffect } from "react-use"

import { idleCb } from "@/3514/utils"
import type { TEmptyCallback } from "@/shared/types/functions"

import { useCoachingChatContext as useChatContext } from "../context"
import {
    useCoachingChatConversationApiMode as useConversationApiMode,
    useCoachingChatMessagesIdsSelector as useMessagesIdsSelector
} from "../store"
import { ECoachingChatConversationApiMode as EConversationApiMode } from "../types"

import { CoachingChatMessageContainer as MessageContainer } from "./coaching-chat.message.container"

const Messages: FC = (): ReactElement => {
    const {
        entityId,
        wasCompleted: wasChatCompleted,
        conversation: { feedRef, feedScrollerHook }
    }: ReturnType<typeof useChatContext> = useChatContext()

    const scrollFeedToBottom: TEmptyCallback = feedScrollerHook(feedRef)

    const messagesIds: string[] = useMessagesIdsSelector(entityId)

    const apiMode: EConversationApiMode = useConversationApiMode()

    const isSocketMode: boolean = eq(apiMode, EConversationApiMode.Sockets)

    useShallowCompareEffect((): void => {
        idleCb(scrollFeedToBottom, 20)
    }, [scrollFeedToBottom, messagesIds.length])

    return wasChatCompleted ? (
        <Fragment>
            {map<string, ReactElement>(
                messagesIds,
                (m: string): ReactElement => (
                    <MessageContainer key={m} clientMessageId={m} />
                )
            )}
        </Fragment>
    ) : (
        <LazyMotion features={domAnimation} key={`messages-${isSocketMode}`}>
            <AnimatePresence initial={false} mode="popLayout">
                {map<string, ReactElement>(
                    messagesIds,
                    (m: string): ReactElement => (
                        <MessageContainer key={m} clientMessageId={m} />
                    )
                )}
            </AnimatePresence>
        </LazyMotion>
    )
}

Messages.displayName = "CoachingChatMessages"

export { Messages as CoachingChatMessages }
